// src/pages/AvatarSettings.js
import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle
} from "react";
import axiosInstance from '../utils/axiosInstance';
import { hasPermission } from '../utils/permissionUtils';
import { useParams } from 'react-router-dom';
import {
  Divider,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  Container
} from "@mui/material";

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';

const AvatarSettings = forwardRef(
  ({ isSetupMode = false, chatbotId, onComplete, onError }, ref) => {
    const theme = useTheme();
    
    /* State Management */
    const [avatars, setAvatars] = useState([]);
    const [pagination, setPagination] = useState({
      page: 1,
      per_page: 12,
      total_pages: 1,
      total_items: 0
    });
    const [filters, setFilters] = useState({
      search: "",
      type: "",
      category: ""
    });
    const [filterOptions, setFilterOptions] = useState({
      types: [],
      categories: []
    });
    const [loading, setLoading] = useState(true);
    const { chatbotId: paramsChatbotId } = useParams();
    let chatBotId = chatbotId ? chatbotId : paramsChatbotId;

    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [savedAvatar, setSavedAvatar] = useState(null);
    const [errorDiv, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    /* Check Permissions */
    useEffect(() => {
      const checkPermissions = async () => {
        setCheckingPermissions(true);
        try {
          const viewPerm = await hasPermission('avatar', 'view');
          const modifyPerm = await hasPermission('avatar', 'add|edit');
          setCanView(viewPerm);
          setCanModify(modifyPerm);
        } catch (error) {
          console.error('Error checking permissions:', error);
          setCanView(false);
          setCanModify(false);
        } finally {
          setCheckingPermissions(false);
        }
      };
      checkPermissions();
    }, []);

    /* Fetch User's Saved Avatar */
    const fetchUserAvatar = useCallback(() => {
      const params = chatBotId ? { chatbot_id: chatBotId } : {};
      axiosInstance
        .get(`/get_avatar_configurations`, { params })
        .then((response) => {
          const responseData =
            typeof response.data.data === "string"
              ? JSON.parse(response.data.data)
              : response.data.data;
          if (Array.isArray(responseData) && responseData.length > 0) {
            const savedAvatarName = responseData[0].avatar_name;
            const savedAvatarImgName = `${savedAvatarName}.png`;
            const finalAvatarData = {
              id: "saved",
              avatar_img_name: savedAvatarImgName,
              avatar_name: savedAvatarName
            };
            setSavedAvatar(finalAvatarData);
            setSelectedAvatar(finalAvatarData);
          } else {
            setSavedAvatar(null);
          }
        })
        .catch((error) => {
          console.error("Error fetching user avatar configuration:", error);
        });
    }, [chatBotId]);

    /* Fetch Available Avatars */
    const fetchAvailableAvatars = useCallback(() => {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.per_page,
        search: filters.search,
        type: filters.type,
        category: filters.category
      });

      axiosInstance
        .get(`/get_all_avatars?${params.toString()}`)
        .then((response) => {
          const data = response.data.data;
          setAvatars(data.avatars);
          setPagination((prev) => ({
            ...prev,
            total_pages: data.pagination.total_pages,
            total_items: data.pagination.total_items
          }));
          if (
            pagination.page === 1 &&
            !filters.search &&
            !filters.type &&
            !filters.category
          ) {
            setFilterOptions(data.filter_options);
          }
        })
        .catch((error) => {
          console.error("Error fetching available avatars:", error);
          const errorMsg =
            error.response?.data?.message || "Error fetching avatar list.";
          if (isSetupMode) onError(errorMsg);
          else setError(errorMsg);
        })
        .finally(() => {
          setLoading(false);
        });
    }, [pagination.page, pagination.per_page, filters, isSetupMode, onError]);

    /* Initial Load */
    useEffect(() => {
      if (!isSetupMode) {
        document.title = "Avatar Setting | KlonIT Web Application";
      }
      fetchUserAvatar();
    }, [isSetupMode, fetchUserAvatar]);

    useEffect(() => {
      const timer = setTimeout(() => {
        fetchAvailableAvatars();
      }, 500);
      return () => clearTimeout(timer);
    }, [fetchAvailableAvatars]);

    /* Handlers */
    const handleAvatarSelection = (avatar) => {
      setSelectedAvatar(avatar);
      setError(null);
    };

    const triggerSave = async () => {
      if (!selectedAvatar || !selectedAvatar.avatar_img_name) {
        if (savedAvatar) {
          setSelectedAvatar(savedAvatar);
        } else {
          const noSelectionMsg = "No avatar selected. Please choose an avatar.";
          if (isSetupMode) onError(noSelectionMsg);
          else setError(noSelectionMsg);
          return false;
        }
      }

      setIsSaving(true);
      setError(null);

      const imagePath = `images/avatars/${selectedAvatar.avatar_img_name}`;
      const dataToSave = { avatar_name: imagePath, chatbot_id: chatBotId };

      try {
        const response = await axiosInstance.post(
          `/update_avatar_configuration`,
          dataToSave
        );

        try {
          let userData = JSON.parse(localStorage.getItem("user") || "{}");
          userData.avatar_settings = { avatar_name: imagePath };
          userData.chatbot_setup_configuration = {
            ...userData.chatbot_setup_configuration,
            is_avatar_set: true
          };
          localStorage.setItem("user", JSON.stringify(userData));
        } catch (e) {
          console.error("Failed to update localStorage with avatar:", e);
          if (isSetupMode) {
            onError("Avatar saved, but failed to update local session data.");
          }
        }

        fetchUserAvatar();
        fetchAvailableAvatars();

        if (isSetupMode) {
          if (onComplete) onComplete({ nextSlug: 'set-audio-language' });
          return true;
        } else {
          setError(response.data.message || "Avatar saved successfully!");
          setTimeout(() => setError(null), 2000);
        }
        return true;
      } catch (error) {
        console.error("Error saving avatar configuration:", error);
        const errorMsg =
          error.response?.data?.message || "Error saving avatar configuration.";
        if (isSetupMode) onError(errorMsg);
        else setError(errorMsg);
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      triggerSave
    }));

    const handlePageChange = (event, value) => {
      setPagination((prev) => ({ ...prev, page: value }));
    };

    const handleFilterChange = (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    };

    /* Loading & Permission Checks */
    if (checkingPermissions) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
          <Typography ml={2}>Checking permissions...</Typography>
        </Box>
      );
    }

    if (!canView) {
      return (
        <Container maxWidth="md" sx={{ mt: 6 }}>
          <Alert severity="error">
            <Typography variant="h6">Access Denied</Typography>
            You don't have permission to view avatar settings. Please contact your administrator.
          </Alert>
        </Container>
      );
    }

    /* Render */
    return (

      <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper', height: '100%' }}>
        
        <Box sx={isSetupMode ? { p: 0 } : { p: 3 }}>
          {!isSetupMode && (
            <Box mb={3}>
              <Typography variant="h4" component="h1" color="textPrimary">
                <strong>Avatar</strong> Setting
              </Typography>
            </Box>
          )}
  
            <Divider sx={{ mb: 3, borderColor: "divider" }} />

            <Typography
              variant="h5"
              sx={{
                textAlign: isSetupMode ? "center" : "left",
                mb: 2,
                fontWeight: 600
              }}
              color="textPrimary"
            >
              Choose your avatar
              
            </Typography>

            {isSetupMode && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mb: 3 }}
              >
                Select an avatar that will represent your chatbot.
              </Typography>
            )}
            
            {/* Filters */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by Name, Type, Category"
                  variant="outlined"
                  size="small"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={filters.type}
                    label="Type"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Types</em>
                    </MenuItem>
                    {filterOptions.types.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    label="Category"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {filterOptions.categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Saved Avatar Section */}
            {savedAvatar && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }} color="textPrimary">
                  Selected Avatar
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Card
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        border: `3px solid ${theme.palette.success.main}`,
                        boxShadow: 3
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          color: theme.palette.success.main
                        }}
                      >
                        <CheckCircleIcon fontSize="large" />
                      </Box>
                      <Box
                          sx={{
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9f9f9",
                            overflow: "hidden",
                            borderRadius: "12px"
                          }}
                        >
                          <Box
                            component="img"
                            src={`/images/avatars/${savedAvatar.avatar_img_name}`}
                            alt={savedAvatar.avatar_name}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain"
                            }}
                          />
                      </Box>
                      <CardContent sx={{ p: 1.5 }}>
                        {/* <Typography variant="body2" fontWeight={600}>
                          {savedAvatar.avatar_name}
                        </Typography> */}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Available Avatars */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
                <Typography ml={2}>Loading avatars...</Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }} color="textPrimary">
                  Available Avatars
                </Typography>
                <Grid container spacing={2}>
                  {avatars.length > 0 ? (
                    avatars.map((avatar) => (
                      <Grid item xs={6} md={3} key={avatar.id}>
                        <Card
                          onClick={() => handleAvatarSelection(avatar)}
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            border:
                              selectedAvatar?.avatar_img_name === avatar.avatar_img_name
                                ? `3px solid ${theme.palette.primary.main}`
                                : `1px solid ${theme.palette.divider}`,
                            transition: "all 0.2s",
                            "&:hover": {
                              boxShadow: 4,
                              transform: "translateY(-2px)"
                            }
                          }}
                        >
                          {selectedAvatar?.avatar_img_name === avatar.avatar_img_name && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                color: theme.palette.primary.main
                              }}
                            >
                              <CheckCircleIcon fontSize="large" />
                            </Box>
                          )}
                          <Box
                            sx={{
                              height: 200,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9f9f9",
                              overflow: "hidden",
                              borderRadius: "12px"
                            }}
                            >
                            <Box
                              component="img"
                              src={`/images/avatars/${avatar.avatar_img_name}`}
                              alt={avatar.avatar_name}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain"
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: isSetupMode ? 1 : 1.5 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ fontSize: isSetupMode ? "0.9rem" : "1rem" }}
                            >
                              {avatar.avatar_name}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography color="text.secondary">
                          No avatars found matching your criteria.
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pagination.total_pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

            {/* Save Button & Error Messages */}
            {canModify && !isSetupMode && (
              <Box mt={3}>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={triggerSave}
                  disabled={isSaving || !selectedAvatar || !selectedAvatar.id}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
                {errorDiv && (
                  <Alert severity={errorDiv.includes("success") ? "success" : "warning"} sx={{ mt: 2 }}>
                    {errorDiv}
                  </Alert>
                )}
              </Box>
            )}

            {isSetupMode && errorDiv && (
              <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                {errorDiv}
              </Typography>
            )}
        </Box>
      </Paper>
    );
  }
);

export default AvatarSettings;